import { Minus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PrimaryButton, SecondaryButton, TextButton } from '../components/Button';
import { Card } from '../components/Card';
import { copy } from '../copy';
import { openExternal, telHref } from '../lib/links';
import { allHandled, findStopById } from '../state/route';
import { UNDO_WINDOW_MS, useAppStore } from '../state/store';

const PLANNING_PHONE: string = import.meta.env.VITE_PLANNING_PHONE ?? '';

/** H3 — bevestiging. Herhaalt reden en beide keuzes letterlijk, met de juiste variant. */
export function FailConfirmScreen() {
  const result = useAppStore((s) => s.failResult);
  const route = useAppStore((s) => s.route);
  const queue = useAppStore((s) => s.queue);
  const navigate = useAppStore((s) => s.navigate);
  const setFailResult = useAppStore((s) => s.setFailResult);
  const undo = useAppStore((s) => s.undo);

  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Date.now() - started), 500);
    return () => window.clearInterval(timer);
  }, [result?.clientActionId]);

  if (!result) return null;

  const stop = route ? findStopById(route, result.stopId) : null;
  // De server bepaalt de eerstvolgende bezorgdag (A-04); tot zijn antwoord binnen is toont de
  // app "morgenavond", daarna de datum die er werkelijk uit kwam.
  const plannedFor = result.redeliver ? (stop?.redeliveryPlannedFor ?? null) : undefined;

  const item = queue.find((candidate) => candidate.clientActionId === result.clientActionId);
  const canUndo = item?.state === 'waiting' && elapsed < UNDO_WINDOW_MS;

  const goOn = () => {
    setFailResult(null);
    navigate(route && allHandled(route) ? 'return' : 'ride');
  };

  return (
    <div className="screen" data-screen="failconfirm">
      <div className="screen__body screen__body--center pad-x">
        <div className="stack" style={{ gap: 16 }}>
          <span
            aria-hidden
            style={{
              width: 76,
              height: 76,
              borderRadius: 20,
              background: 'var(--color-terracotta)',
              color: 'var(--on-accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Minus size={34} strokeWidth={2} />
          </span>
          <h1 className="t-serif" style={{ fontSize: 30 }}>
            {copy.fail.confirmTitle}
          </h1>
          <div style={{ fontSize: 19, fontWeight: 600 }}>
            {copy.fail.confirmSubject(result.partnerName, result.stopOrder)}
          </div>
          <Card>
            <div className="stack" style={{ gap: 12 }}>
              <div style={{ fontSize: 17, lineHeight: 1.45 }}>
                {copy.fail.confirmReason(copy.fail.reasons[result.reason])}
              </div>
              <div style={{ fontSize: 17, lineHeight: 1.45 }}>
                {result.invoiceAnyway ? copy.fail.confirmInvoiceYes : copy.fail.confirmInvoiceNo}
              </div>
              <div style={{ fontSize: 17, lineHeight: 1.45 }}>
                {plannedFor === undefined
                  ? copy.fail.confirmRedeliverNo
                  : copy.fail.confirmRedeliverYes(plannedFor)}
              </div>
            </div>
          </Card>
          <p className="t-caption" style={{ color: 'var(--text-65)', lineHeight: 1.5, margin: 0 }}>
            {copy.fail.confirmFootnote}
          </p>
        </div>
      </div>
      <div className="screen__foot">
        <PrimaryButton onClick={goOn}>{copy.fail.continue}</PrimaryButton>
        <SecondaryButton
          disabledReason={PLANNING_PHONE ? null : copy.stop.noPhone}
          onClick={() => openExternal(telHref(PLANNING_PHONE))}
        >
          {copy.fail.callPlanning}
        </SecondaryButton>
        {canUndo ? (
          <TextButton
            variant="muted"
            center
            onClick={() => void undo(result.clientActionId).then(() => navigate('stop'))}
          >
            {copy.scanResult.undo}
          </TextButton>
        ) : null}
      </div>
    </div>
  );
}
